/**
 * Partner category groups (DV-159). The filter-options endpoints return one option per
 * category group: `tagId` is the first tag of the group, `tagIds` every tag of it. The
 * services query still filters by tag ids (any of them matches), so choosing a group
 * sends all its tags. A backend without groups returns no `tagIds`: the option is its tag.
 */

export type PartnerCategoryGroupOption = {
  tagId: number;
  name: string;
  tagIds?: number[];
};

/** A filter option that stands for a set of tags. */
export type TagGroup = { tagIds: readonly number[] };

/** The tags a category option stands for. */
export function partnerCategoryTagIds(
  option: Pick<PartnerCategoryGroupOption, "tagId" | "tagIds">,
): number[] {
  return option.tagIds?.length ? [...option.tagIds] : [option.tagId];
}

/** A group is selected when any of its tags is in the selection. */
export function isTagGroupSelected(
  selectedTagIds: readonly number[],
  group: TagGroup,
): boolean {
  return group.tagIds.some((tagId) => selectedTagIds.includes(tagId));
}

/** Selecting a group adds all its tags; unselecting it removes them all. */
export function toggleTagGroup(
  selectedTagIds: readonly number[],
  group: TagGroup,
): number[] {
  if (isTagGroupSelected(selectedTagIds, group)) {
    return selectedTagIds.filter((tagId) => !group.tagIds.includes(tagId));
  }
  return [
    ...selectedTagIds,
    ...group.tagIds.filter((tagId) => !selectedTagIds.includes(tagId)),
  ];
}

/** The groups the selection holds, in option order. */
export function selectedTagGroups<T extends TagGroup>(
  selectedTagIds: readonly number[],
  groups: readonly T[],
): T[] {
  return groups.filter((group) => isTagGroupSelected(selectedTagIds, group));
}

/**
 * Active category filters: one per selected group. Before the groups load, a
 * non-empty selection counts as one filter.
 */
export function countSelectedTagGroups(
  selectedTagIds: readonly number[],
  groups: readonly TagGroup[],
): number {
  if (groups.length === 0) {
    return selectedTagIds.length > 0 ? 1 : 0;
  }
  return selectedTagGroups(selectedTagIds, groups).length;
}

/**
 * Tag ids for the services query from chosen option ids (the options' `tagId`): the
 * union of the tags of each chosen group. An id no option knows passes through as is.
 */
export function expandPartnerCategoryIds(
  optionIds: readonly number[],
  options: readonly Pick<PartnerCategoryGroupOption, "tagId" | "tagIds">[],
): number[] {
  const byId = new Map(options.map((option) => [option.tagId, option]));
  const tagIds = optionIds.flatMap((id) => {
    const option = byId.get(id);
    return option ? partnerCategoryTagIds(option) : [id];
  });
  return [...new Set(tagIds)];
}
